import "./CareManagerVideoCall.css";
import AgoraRTC, {
  useClientEvent,
  useConnectionState,
  useJoin,
  useRemoteUsers,
  useRemoteUserTrack,
  useRTCClient,
} from "agora-rtc-react";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
} from "react-icons/fa";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import logo from "../../assets/Numente.png";
import useWaitingRoomNotifications from "../../hooks/useWaitingRoomNotifications";
import WaitingRoomNotificationCard from "./WaitingRoomNotificationCard";
import WaitingRoomQueuePanel from "./WaitingRoomQueuePanel";
import {
  addTranscription,
  startTranscribing,
  stopTranscribing,
} from "./helpers";
import protoRoot from "./parseSTT";

const getDisplayName = (person, fallback) => {
  const name = `${person?.firstname ?? ""} ${person?.lastname ?? ""}`.trim();
  return name || person?.patientName || person?.name || person?.email || fallback;
};

const getInitials = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const isSameUid = (left, right) => String(left) === String(right);

// Agora speech-to-text bots join the channel as participants; never treat them as the patient.
const TRANSCRIPTION_BOT_UIDS = new Set(["100", "101"]);
const isTranscriptionBot = (remoteUid) =>
  TRANSCRIPTION_BOT_UIDS.has(String(remoteUid));

const getAppointmentEndTime = (apt) => {
  if (!apt?.appointmenttime) return null;

  if (apt.appointmentEndTime) {
    return new Date(apt.appointmentEndTime);
  }

  const startTime = new Date(apt.appointmenttime);
  const durationMinutes = apt.sessionLength || 30;

  return new Date(startTime.getTime() + durationMinutes * 60000);
};

const hasScheduledEndTimePassed = (apt) => {
  const endTime = getAppointmentEndTime(apt);
  if (!endTime || Number.isNaN(endTime.getTime())) return true;

  return new Date() >= endTime;
};

const isTrackPublished = (client, track) =>
  Boolean(
    track &&
      client?.localTracks?.some(
        (publishedTrack) => publishedTrack.getTrackId() === track.getTrackId()
      )
  );

const unpublishTrack = async (client, track) => {
  if (!client || !track) return;

  try {
    await client.unpublish(track);
  } catch {
    // Track may already be unpublished.
  }
};

const publishTrack = async (client, track) => {
  if (!client || !track || isTrackPublished(client, track)) return;

  try {
    await client.publish(track);
  } catch (error) {
    console.warn("Unable to publish track:", error);
  }
};

const closeTrack = async (track, { stopFirst = false, muteFirst = false } = {}) => {
  if (!track) return;

  try {
    if (muteFirst && track.setMuted) {
      await track.setMuted(true);
    }
  } catch {
    // Ignore mute failures during cleanup.
  }

  try {
    if (track.setEnabled) {
      await track.setEnabled(false);
    }
  } catch {
    // Ignore enable failures during cleanup.
  }

  try {
    if (stopFirst && track.stop) {
      track.stop();
    }
  } catch {
    // Ignore stop failures during cleanup.
  }

  try {
    track.close();
  } catch {
    // Ignore close failures during cleanup.
  }
};

const PatientMainPanel = ({ patientName, patientRemoteUser, speakerId }) => {
  const previewRef = useRef(null);
  const [mediaRevision, setMediaRevision] = useState(0);
  const [graceExpired, setGraceExpired] = useState(false);
  const client = useRTCClient();

  const { track: patientVideoTrack, isLoading: isPatientVideoLoading } =
    useRemoteUserTrack(patientRemoteUser, "video");
  const { track: patientAudioTrack } = useRemoteUserTrack(
    patientRemoteUser,
    "audio"
  );

  const bumpMediaRevision = useCallback(() => {
    setMediaRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    setGraceExpired(false);

    const timeoutId = window.setTimeout(() => {
      setGraceExpired(true);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [patientRemoteUser?.uid, mediaRevision]);

  useClientEvent(client, "user-published", (user) => {
    if (!isSameUid(user.uid, patientRemoteUser?.uid)) return;
    setGraceExpired(false);
    bumpMediaRevision();
  });

  useClientEvent(client, "user-unpublished", (user) => {
    if (!isSameUid(user.uid, patientRemoteUser?.uid)) return;
    bumpMediaRevision();
  });

  const isJoinGracePeriod = !graceExpired;

  const patientCameraOn = Boolean(
    patientVideoTrack || (patientRemoteUser?.hasVideo && isPatientVideoLoading)
  );
  const patientMicOn = Boolean(
    patientRemoteUser?.hasAudio && patientAudioTrack
  );

  const isPatientCameraOff =
    !patientCameraOn && !patientRemoteUser?.hasVideo && !isJoinGracePeriod;

  const isPatientVideoPending =
    !patientVideoTrack &&
    (patientRemoteUser?.hasVideo || isPatientVideoLoading || isJoinGracePeriod);

  useEffect(() => {
    if (!patientVideoTrack || !previewRef.current) return undefined;

    try {
      patientVideoTrack.play(previewRef.current);
    } catch (error) {
      console.warn("Unable to play patient camera preview:", error);
    }

    return () => {
      patientVideoTrack.stop();
    };
  }, [patientVideoTrack, mediaRevision]);

  useEffect(() => {
    if (!patientAudioTrack) return undefined;

    if (!patientRemoteUser?.hasAudio) {
      patientAudioTrack.stop();
      return undefined;
    }

    patientAudioTrack.play();

    if (speakerId) {
      try {
        const playback = patientAudioTrack.setPlaybackDevice(speakerId);
        if (playback?.catch) {
          playback.catch((error) =>
            console.warn("Unable to set speaker device:", error)
          );
        }
      } catch (error) {
        console.warn("Unable to set speaker device:", error);
      }
    }

    return () => {
      patientAudioTrack.stop();
    };
  }, [patientAudioTrack, patientRemoteUser?.hasAudio, speakerId, mediaRevision]);

  return (
    <div className="cm-video-session-remote">
      <div
        className={`cm-video-session-preview-host cm-video-session-remote-video${
          patientVideoTrack ? " is-visible" : ""
        }`}
        ref={previewRef}
      />

      {isPatientVideoPending && (
        <div className="cm-video-session-main-placeholder">
          <p className="cm-video-session-main-placeholder-text">
            Connecting camera...
          </p>
        </div>
      )}

      {isPatientCameraOff && (
        <div className="cm-video-session-main-placeholder">
          <span className="cm-video-session-main-initials">
            {getInitials(patientName)}
          </span>
        </div>
      )}

      <span className="cm-video-session-participant-label">
        {!patientMicOn ? (
          <span className="cm-video-session-participant-mic-off" title="Microphone off">
            <FaMicrophoneSlash />
          </span>
        ) : null}
        {patientName}
      </span>
    </div>
  );
};

const ProviderSelfViewPip = ({
  cameraError,
  cameraOn,
  isCameraLoading,
  localPreviewRef,
  micOn,
  providerName,
  showLocalPreview,
  useDefaultCamera,
}) => {
  const showCameraOffPlaceholder = !cameraOn;
  const showLoadingPlaceholder = cameraOn && !showLocalPreview && isCameraLoading;
  const showErrorPlaceholder =
    cameraOn && !showLocalPreview && Boolean(cameraError) && useDefaultCamera;

  return (
    <div className="cm-video-session-pip">
      {showLocalPreview ? (
        <div
          className="cm-video-session-preview-host cm-video-session-pip-video is-visible"
          ref={localPreviewRef}
        />
      ) : null}

      {showLoadingPlaceholder && (
        <div className="cm-video-session-pip-placeholder">
          <p className="cm-video-session-pip-name">Starting camera...</p>
        </div>
      )}

      {showErrorPlaceholder && (
        <div className="cm-video-session-pip-placeholder">
          <p className="cm-video-session-pip-name">
            Unable to access camera. Check browser permissions.
          </p>
        </div>
      )}

      {showCameraOffPlaceholder && (
        <div className="cm-video-session-pip-placeholder cm-video-session-pip-placeholder--camera-off">
          <FaVideoSlash aria-hidden />
        </div>
      )}

      {!micOn ? (
        <span
          className="cm-video-session-pip-mic-off"
          title="Microphone off">
          <FaMicrophoneSlash />
        </span>
      ) : null}
    </div>
  );
};

const CareManagerVideoCall = ({
  appId,
  appointment,
  authToken,
  channelName,
  devicePreferences,
  enableTranscription = false,
  handleClose,
  isJoin,
  markCompletedOnEnd = true,
  provider,
  token,
  uid,
}) => {
  const [cameraOn, setCameraOn] = useState(devicePreferences?.cameraOn ?? false);
  const [micOn, setMicOn] = useState(devicePreferences?.micOn ?? false);
  const [useDefaultCamera, setUseDefaultCamera] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const [patientDisconnected, setPatientDisconnected] = useState(false);
  const [patientMediaEpoch, setPatientMediaEpoch] = useState(0);
  const [localCameraTrack, setLocalCameraTrack] = useState(null);
  const [localMicrophoneTrack, setLocalMicrophoneTrack] = useState(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const localPreviewRef = useRef(null);
  const localCameraTrackRef = useRef(null);
  const localMicrophoneTrackRef = useRef(null);
  // Therapy sessions keep a transcript for Session History (same as the old
  // therapist call screen); care manager check-ins do not.
  const transcriptRef = useRef([]);
  const transcriptionTaskRef = useRef(null);
  const transcriptionStartedRef = useRef(false);

  const appointmentId = appointment?._id;
  const providerName = useMemo(
    () => getDisplayName(provider, "Provider"),
    [provider]
  );

  const {
    handleAdmit,
    handleDismiss,
    isAdmitting,
    isDismissing,
    isQueuePanelOpen,
    setIsQueuePanelOpen,
    showWaitingIndicator,
    visiblePatient,
    waitingCount,
    waitingPatients,
  } = useWaitingRoomNotifications(appointmentId);

  const patientName = useMemo(() => {
    if (visiblePatient?.patientName) return visiblePatient.patientName;
    return getDisplayName(appointment?.user, "Patient");
  }, [appointment?.user, visiblePatient]);

  const cameraTrackConfig = useMemo(() => {
    if (useDefaultCamera || !devicePreferences?.cameraId) return undefined;
    return { cameraId: devicePreferences.cameraId };
  }, [devicePreferences?.cameraId, useDefaultCamera]);

  const micTrackConfig = useMemo(
    () =>
      devicePreferences?.micId
        ? { microphoneId: devicePreferences.micId }
        : undefined,
    [devicePreferences?.micId]
  );

  const client = useRTCClient();
  const connectionState = useConnectionState();
  const remoteUsers = useRemoteUsers();

  useEffect(() => {
    localCameraTrackRef.current = localCameraTrack;
  }, [localCameraTrack]);

  useEffect(() => {
    localMicrophoneTrackRef.current = localMicrophoneTrack;
  }, [localMicrophoneTrack]);

  const patientRemoteUser = useMemo(
    () =>
      remoteUsers.find(
        (remoteUser) =>
          !isSameUid(remoteUser.uid, uid) && !isTranscriptionBot(remoteUser.uid)
      ),
    [remoteUsers, uid]
  );

  useClientEvent(client, "user-joined", (user) => {
    if (isSameUid(user.uid, uid) || isTranscriptionBot(user.uid)) return;
    setPatientDisconnected(false);
    setPatientMediaEpoch((epoch) => epoch + 1);
  });

  useClientEvent(client, "user-left", (user) => {
    if (isSameUid(user.uid, uid) || isTranscriptionBot(user.uid)) return;
    setPatientDisconnected(true);
    setPatientMediaEpoch((epoch) => epoch + 1);
  });

  useClientEvent(client, "stream-message", (_senderUid, stream) => {
    if (!enableTranscription) return;

    try {
      const streamData = protoRoot.Agora.SpeechToText.Text.decode(stream);
      const translation = streamData?.trans?.[0];

      if (
        streamData.dataType === "translate" &&
        translation?.lang === "en-US" &&
        translation?.isFinal
      ) {
        transcriptRef.current = [
          ...transcriptRef.current,
          {
            isRemote: !isSameUid(streamData.uid, uid),
            message: translation.texts.join(""),
          },
        ];
      }
    } catch (error) {
      console.warn("Unable to decode transcription message:", error);
    }
  });

  useEffect(() => {
    if (
      !enableTranscription ||
      connectionState !== "CONNECTED" ||
      transcriptionStartedRef.current ||
      !channelName ||
      !token ||
      !uid
    ) {
      return;
    }

    transcriptionStartedRef.current = true;
    startTranscribing(
      channelName,
      (data) => {
        transcriptionTaskRef.current = data;
      },
      () => {},
      token,
      uid
    );
  }, [channelName, connectionState, enableTranscription, token, uid]);

  const showLocalPreview = cameraOn && Boolean(localCameraTrack);

  useJoin(
    {
      appid: appId,
      channel: channelName,
      token: token ?? null,
      uid: uid ?? null,
    },
    isJoin
  );

  useEffect(() => {
    let cancelled = false;

    const syncCameraTrack = async () => {
      if (!cameraOn) {
        const existingTrack = localCameraTrackRef.current;
        if (!existingTrack) return;

        await unpublishTrack(client, existingTrack);
        await closeTrack(existingTrack, { stopFirst: true });

        if (!cancelled) {
          setLocalCameraTrack(null);
          setCameraError(null);
          setIsCameraLoading(false);
        }
        return;
      }

      if (localCameraTrackRef.current) {
        if (connectionState === "CONNECTED") {
          await publishTrack(client, localCameraTrackRef.current);
        }
        return;
      }

      if (!cancelled) {
        setIsCameraLoading(true);
        setCameraError(null);
      }

      try {
        const createdTrack = await AgoraRTC.createCameraVideoTrack(cameraTrackConfig);
        if (cancelled) {
          await closeTrack(createdTrack, { stopFirst: true });
          return;
        }

        localCameraTrackRef.current = createdTrack;
        setLocalCameraTrack(createdTrack);

        if (connectionState === "CONNECTED") {
          await publishTrack(client, createdTrack);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Unable to create camera track:", error);
          setCameraError(error);
        }
      } finally {
        if (!cancelled) {
          setIsCameraLoading(false);
        }
      }
    };

    syncCameraTrack();

    return () => {
      cancelled = true;
    };
  }, [cameraOn, cameraTrackConfig, client, connectionState]);

  useEffect(() => {
    let cancelled = false;

    const syncMicrophoneTrack = async () => {
      if (!micOn) {
        const existingTrack = localMicrophoneTrackRef.current;
        if (!existingTrack) return;

        await unpublishTrack(client, existingTrack);
        await closeTrack(existingTrack, { muteFirst: true });

        if (!cancelled) {
          setLocalMicrophoneTrack(null);
        }
        return;
      }

      if (localMicrophoneTrackRef.current) {
        try {
          await localMicrophoneTrackRef.current.setMuted(false);
          await localMicrophoneTrackRef.current.setEnabled(true);
        } catch (error) {
          console.warn("Unable to enable microphone track:", error);
        }

        if (connectionState === "CONNECTED") {
          await publishTrack(client, localMicrophoneTrackRef.current);
        }
        return;
      }

      try {
        const createdTrack = await AgoraRTC.createMicrophoneAudioTrack(micTrackConfig);
        if (cancelled) {
          await closeTrack(createdTrack, { muteFirst: true });
          return;
        }

        localMicrophoneTrackRef.current = createdTrack;
        setLocalMicrophoneTrack(createdTrack);

        if (connectionState === "CONNECTED") {
          await publishTrack(client, createdTrack);
        }
      } catch (error) {
        console.warn("Unable to create microphone track:", error);
      }
    };

    syncMicrophoneTrack();

    return () => {
      cancelled = true;
    };
  }, [client, connectionState, micOn, micTrackConfig]);

  useEffect(() => {
    if (connectionState !== "CONNECTED") return undefined;

    const publishLocalTracks = async () => {
      if (cameraOn && localCameraTrackRef.current) {
        await publishTrack(client, localCameraTrackRef.current);
      }

      if (micOn && localMicrophoneTrackRef.current) {
        await publishTrack(client, localMicrophoneTrackRef.current);
      }
    };

    publishLocalTracks();

    return undefined;
  }, [cameraOn, client, connectionState, micOn]);

  useEffect(() => {
    if (!cameraError || useDefaultCamera || !devicePreferences?.cameraId) return;
    console.warn("Selected camera unavailable, falling back to default:", cameraError);
    setUseDefaultCamera(true);
  }, [cameraError, devicePreferences?.cameraId, useDefaultCamera]);

  useEffect(() => {
    if (
      !showLocalPreview ||
      !localCameraTrack ||
      !cameraOn ||
      !localPreviewRef.current
    ) {
      return undefined;
    }

    try {
      localCameraTrack.play(localPreviewRef.current, { mirror: true });
    } catch (error) {
      console.warn("Unable to play local camera preview:", error);
    }

    return () => {
      localCameraTrack.stop();
    };
  }, [cameraOn, localCameraTrack, showLocalPreview]);

  useEffect(
    () => () => {
      const cleanup = async () => {
        await closeTrack(localMicrophoneTrackRef.current, { muteFirst: true });
        await closeTrack(localCameraTrackRef.current, { stopFirst: true });
      };

      cleanup();
    },
    []
  );

  useEffect(() => {
    const releaseMediaOnExit = () => {
      closeTrack(localMicrophoneTrackRef.current, { muteFirst: true });
      closeTrack(localCameraTrackRef.current, { stopFirst: true });
    };

    window.addEventListener("beforeunload", releaseMediaOnExit);
    window.addEventListener("pagehide", releaseMediaOnExit);

    return () => {
      window.removeEventListener("beforeunload", releaseMediaOnExit);
      window.removeEventListener("pagehide", releaseMediaOnExit);
    };
  }, []);

  useEffect(() => {
    const timerId = setInterval(() => {
      setDuration((current) => current + 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  const toggleCamera = () => {
    setCameraOn((current) => !current);
  };

  const toggleMic = () => {
    setMicOn((current) => !current);
  };

  const endSession = async () => {
    if (isEnding) return;

    setIsEnding(true);

    try {
      await unpublishTrack(client, localMicrophoneTrackRef.current);
      await unpublishTrack(client, localCameraTrackRef.current);
      await closeTrack(localMicrophoneTrackRef.current, { muteFirst: true });
      await closeTrack(localCameraTrackRef.current, { stopFirst: true });
      setLocalMicrophoneTrack(null);
      setLocalCameraTrack(null);

      try {
        if (
          connectionState === "CONNECTED" ||
          connectionState === "CONNECTING"
        ) {
          await client.leave();
        }
      } catch (error) {
        console.warn("Unable to leave Agora channel:", error);
      }

      if (enableTranscription) {
        const task = transcriptionTaskRef.current;
        if (task) {
          stopTranscribing(task.builderToken, task.taskId);
        }

        if (appointmentId && transcriptRef.current.length > 0) {
          await addTranscription(
            appointmentId,
            transcriptRef.current,
            appointment?.specialistid ?? provider?.providerId,
            appointment?.user_fk ?? appointment?.user?._id
          );
        }
      }

      if (
        markCompletedOnEnd &&
        appointmentId &&
        authToken &&
        hasScheduledEndTimePassed(appointment)
      ) {
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
          { status: "Completed" },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
    } catch (error) {
      console.error("Failed to update appointment status:", error);
    } finally {
      handleClose();
    }
  };

  return (
    <div className="cm-video-session">
      <header className="cm-video-session-header">
        <img alt="Numente" src={logo} />
      </header>

      <div className="cm-video-session-stage">
        <div className="cm-video-session-main">
          {patientRemoteUser ? (
            <PatientMainPanel
              key={`${patientRemoteUser.uid}-${patientMediaEpoch}`}
              patientName={patientName}
              patientRemoteUser={patientRemoteUser}
              speakerId={devicePreferences?.speakerId}
            />
          ) : patientDisconnected ? (
            <div className="cm-video-session-empty">
              <p>{patientName} left the call</p>
            </div>
          ) : (
            <div className="cm-video-session-empty">
              <p>Waiting for {patientName} to join…</p>
            </div>
          )}

          <ProviderSelfViewPip
            cameraError={cameraError}
            cameraOn={cameraOn}
            isCameraLoading={isCameraLoading}
            localPreviewRef={localPreviewRef}
            micOn={micOn}
            providerName={providerName}
            showLocalPreview={showLocalPreview}
            useDefaultCamera={useDefaultCamera}
          />

          {visiblePatient && !isQueuePanelOpen && (
            <WaitingRoomNotificationCard
              isAdmitting={isAdmitting}
              isDismissing={isDismissing}
              onAdmit={handleAdmit}
              onDismiss={handleDismiss}
              patient={visiblePatient}
            />
          )}

          {showWaitingIndicator && !isQueuePanelOpen && (
            <button
              className="cm-waiting-room-indicator"
              onClick={() => setIsQueuePanelOpen(true)}
              type="button">
              <span className="cm-waiting-room-indicator-dot" />
              {waitingCount} waiting
            </button>
          )}

          {isQueuePanelOpen && (
            <WaitingRoomQueuePanel
              isAdmitting={isAdmitting}
              onAdmit={handleAdmit}
              onClose={() => setIsQueuePanelOpen(false)}
              patients={waitingPatients}
            />
          )}
        </div>

        <div className="cm-video-session-controls">
          <div className="cm-video-session-timer">
            <span className="cm-video-session-timer-dot" />
            <span>
              {`${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, "0")}`}
            </span>
          </div>

          <div className="cm-video-session-media-controls">
            <button
              aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
              className="cm-video-session-media-btn"
              onClick={toggleMic}
              type="button">
              {micOn ? <FaMicrophone /> : <FaMicrophoneSlash />}
            </button>

            <button
              aria-label={cameraOn ? "Turn camera off" : "Turn camera on"}
              className="cm-video-session-media-btn"
              onClick={toggleCamera}
              type="button">
              {cameraOn ? <FaVideo /> : <FaVideoSlash />}
            </button>
          </div>

          <button
            className="cm-video-session-end-btn"
            disabled={isEnding}
            onClick={endSession}
            type="button">
            {isEnding ? "Ending..." : "End Call"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareManagerVideoCall;
